import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Typography, Card, CardContent, Button, IconButton, keyframes } from '@mui/material';
import { useTranslation } from 'react-i18next';
import WikiIcon from '@mui/icons-material/MenuBook';
import DownloadIcon from '@mui/icons-material/CloudDownload';
import DonateIcon from '@mui/icons-material/Favorite';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import YouTubeIcon from '@mui/icons-material/YouTube';
import { useVideoSlider } from '../../hooks/useVideoSlider';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { TEXT_COLORS, BORDER_COLORS } from '../../utils/colors';
import { BUTTON_SIZES } from '../../utils/responsive';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeInScale = keyframes`
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;


interface EventCard {
  icon: React.ReactNode;
  titleKey: string;
  descriptionKey: string;
  buttonKey: string;
  image: string;
  link: string;
}

const eventCards: EventCard[] = [
  {
    icon: <WikiIcon sx={{ fontSize: 40 }} />,
    titleKey: 'economic.cards.wiki.title',
    descriptionKey: 'economic.cards.wiki.description',
    buttonKey: 'economic.cards.wiki.button',
    image: '/img/home/economic-card-11.webp',
    link: '/wiki',
  },
  {
    icon: <DownloadIcon sx={{ fontSize: 40 }} />,
    titleKey: 'economic.cards.downloads.title',
    descriptionKey: 'economic.cards.downloads.description',
    buttonKey: 'economic.cards.downloads.button',
    image: '/img/home/economic-card-2.webp',
    link: '/downloads',
  },
  {
    icon: <DonateIcon sx={{ fontSize: 40 }} />,
    titleKey: 'economic.cards.donate.title',
    descriptionKey: 'economic.cards.donate.description',
    buttonKey: 'economic.cards.donate.button',
    image: '/img/home/economic-card-3.webp',
    link: '/dashboard/payment',
  },
];

// Ролик и его подпись — одной записью: подпись = название ролика на YouTube.
const videos = [
  { id: 'rJda8pQwmSg', titleKey: 'autohunt' },
  { id: 'UZtA2hpU6BY', titleKey: 'outposts' },
  { id: 'i614COorYFQ', titleKey: 'trials' },
  { id: 'B3jkb4tUVWI', titleKey: 'weapons' },
  { id: '1iUIGf8G-HA', titleKey: 'armor' },
  { id: '9gogDtpicWo', titleKey: 'stormLord' },
  { id: 'TzxIRJ1WmPo', titleKey: 'monk' },
  { id: '98IKjS6hNIw', titleKey: 'paladin' },
  { id: 'SaB3pdF6RLI', titleKey: 'earthLord' },
];

const EconomicSection: React.FC = () => {
  const { t } = useTranslation('home');
  const { sliderRef, scrollPrev, scrollNext } = useVideoSlider();
  const { ref: titleRef, isVisible: titleVisible } = useScrollAnimation(0.2);
  const { ref: cardsRef, isVisible: cardsVisible } = useScrollAnimation(0.1);
  const { ref: videoRef, isVisible: videoVisible } = useScrollAnimation(0.1);

  return (
    <Box
      sx={{
        py: { xs: 6, md: 10 },
        px: { xs: 2, lg: 10 },
      }}
    >
      <Box sx={{ maxWidth: '1600px', mx: 'auto' }}>
        {/* Title */}
        <Typography
          ref={titleRef}
          variant="h3"
          sx={{
            textAlign: 'center',
            fontWeight: 800,
            mb: { xs: 4, md: 6 },
            background: 'linear-gradient(135deg, #fff 0%, #a1a1aa 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textTransform: 'uppercase',
            fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
            opacity: 0,
            animation: titleVisible ? `${fadeInUp} 0.8s ease-out both` : 'none',
          }}
        >
          {t('economic.title')}
        </Typography>

        {/* Cards Grid - 3 cards in a row */}
        <Box
          ref={cardsRef}
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
            gap: 4,
            mb: 6,
          }}
        >
          {eventCards.map((card, idx) => (
            <Card
              key={idx}
              component={Link}
              to={card.link}
              sx={{
                position: 'relative',
                overflow: 'hidden',
                background: 'rgba(0, 0, 0, 0.6)',
                backdropFilter: 'blur(10px)',
                border: `1px solid ${BORDER_COLORS.DEFAULT}`,
                borderRadius: 4,
                textDecoration: 'none',
                minHeight: 320,
                display: 'flex',
                flexDirection: 'column',
                transition: 'all 0.4s ease',
                opacity: 0,
                animation: cardsVisible ? `${fadeInUp} 0.6s ease-out ${idx * 0.15}s both` : 'none',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  borderColor: 'rgba(185, 28, 28, 0.6)',
                  boxShadow: '0 20px 40px rgba(185, 28, 28, 0.25)',
                  '& .card-bg': {
                    opacity: 0.4,
                    transform: 'scale(1.1)',
                  },
                },
              }}
            >
              {/* Background Image */}
              <Box
                className="card-bg"
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundImage: `url(${card.image})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  opacity: 0.2,
                  transition: 'all 0.4s ease',
                }}
              />

              <CardContent
                sx={{
                  position: 'relative',
                  zIndex: 1,
                  p: 4,
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                }}
              >
                <Box sx={{ color: '#b91c1c', mb: 2 }}>
                  {card.icon}
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    mb: 2,
                    textTransform: 'uppercase',
                    color: '#fff',
                    fontSize: { xs: '1.2rem', md: '1.5rem' },
                  }}
                >
                  {t(card.titleKey)}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: 'text.secondary', mb: 3, flex: 1, fontSize: '1rem' }}
                >
                  {t(card.descriptionKey)}
                </Typography>
                <Button
                  variant="outlined"
                  size="large"
                  sx={{
                    borderColor: 'rgba(185, 28, 28, 0.5)',
                    color: '#dc2626',
                    textTransform: 'none',
                    height: BUTTON_SIZES.height,
                    fontSize: BUTTON_SIZES.fontSize,
                    '&:hover': {
                      borderColor: '#b91c1c',
                      background: 'rgba(185, 28, 28, 0.15)',
                    },
                  }}
                >
                  {t(card.buttonKey)}
                </Button>
              </CardContent>
            </Card>
          ))}
        </Box>

        {/* Video Section */}
        <Box
          ref={videoRef}
          sx={{
            position: 'relative',
            background: 'rgba(0, 0, 0, 0.4)',
            backdropFilter: 'blur(10px)',
            borderRadius: 4,
            border: `1px solid ${BORDER_COLORS.DEFAULT}`,
            p: { xs: 2, md: 4 },
            opacity: 0,
            animation: videoVisible ? `${fadeInScale} 0.8s ease-out both` : 'none',
            transition: 'all 0.4s ease',
            '&:hover': {
              transform: 'scale(1.01)',
              borderColor: 'rgba(185, 28, 28, 0.4)',
              boxShadow: '0 0 40px rgba(185, 28, 28, 0.2)',
            },
          }}
        >
          {/* Video Section Title */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <YouTubeIcon sx={{ color: '#ff0000', fontSize: 32 }} />
            <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700, textTransform: 'uppercase' }}>
              {t('economic.videos.channel')}
            </Typography>
          </Box>

          {/* Navigation Arrows */}
          <IconButton
            onClick={scrollPrev}
            sx={{
              position: 'absolute',
              left: { xs: 4, md: 16 },
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 10,
              background: 'rgba(0, 0, 0, 0.8)',
              color: '#fff',
              width: 48,
              height: 48,
              '&:hover': {
                background: 'rgba(185, 28, 28, 0.9)',
              },
            }}
          >
            <ChevronLeft fontSize="large" />
          </IconButton>
          <IconButton
            onClick={scrollNext}
            sx={{
              position: 'absolute',
              right: { xs: 4, md: 16 },
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 10,
              background: 'rgba(0, 0, 0, 0.8)',
              color: '#fff',
              width: 48,
              height: 48,
              '&:hover': {
                background: 'rgba(185, 28, 28, 0.9)',
              },
            }}
          >
            <ChevronRight fontSize="large" />
          </IconButton>

          {/* Video Slider */}
          <Box
            ref={sliderRef}
            sx={{
              display: 'flex',
              gap: 3,
              px: { xs: 0, md: 6 },
              overflowX: 'auto',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            {videos.map(({ id, titleKey }) => (
              <Box
                key={id}
                className="video-card"
                sx={{
                  flexShrink: 0,
                  width: { xs: 300, md: 380 },
                }}
              >
                <Box
                  sx={{
                    borderRadius: 3,
                    overflow: 'hidden',
                    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.5)',
                  }}
                >
                  <iframe
                    width="100%"
                    height="220"
                    loading="lazy"
                    src={`https://www.youtube.com/embed/${id}`}
                    title={t(`economic.videos.titles.${titleKey}`)}
                    frameBorder={0}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    style={{ display: 'block' }}
                  />
                </Box>
                <Typography
                  variant="body1"
                  sx={{
                    mt: 1.5,
                    fontWeight: 500,
                    color: TEXT_COLORS.MUTED,
                    fontSize: '0.95rem',
                    textAlign: 'center',
                  }}
                >
                  {t(`economic.videos.titles.${titleKey}`)}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default EconomicSection;
